import { Alert, Divider, Space, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import {
  computeActualTotal,
  computeDrinksTotal,
  computeFoodTotal,
  computeGrandTotal,
  computeInfraTotal,
} from "./calculations";
import { DrinksSection } from "./components/DrinksSection";
import { FoodSection } from "./components/FoodSection";
import { ImportButton } from "./components/ImportButton";
import { InfraSection } from "./components/InfraSection";
import { SnapshotManager } from "./components/SnapshotManager";
import { TagScenarioBar } from "./components/TagScenarioBar";
import { TotalHeader } from "./components/TotalHeader";
import { festivalCalcStore } from "./store";

export const FestivalCalcPage = observer(() => {
  const store = festivalCalcStore;

  useEffect(() => {
    store.loadAll();
  }, []);

  if (store.loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
        <Spin size="large" tip="Lade Kalkulation…" />
      </div>
    );
  }

  if (store.error) {
    return (
      <Alert type="error" message="Fehler beim Laden" description={store.error} style={{ margin: 24 }} />
    );
  }

  const { config, tags, segments, activeTagId, drinkItems, foodItems, infraItems } = store;

  const ingredientsByDrink = new Map(
    drinkItems
      .filter((d) => d.recipe?.objectId)
      .map((d) => [d.objectId!, store.ingredientsByRecipe.get(d.recipe!.objectId!) ?? []])
  );

  const infraTotal = computeInfraTotal(infraItems, activeTagId, tags);
  const foodTotal = computeFoodTotal(foodItems, activeTagId, tags, segments);
  const drinksTotal = computeDrinksTotal(
    drinkItems,
    ingredientsByDrink,
    activeTagId,
    tags,
    segments,
    config.zusagenPercent
  );
  const grandTotal = computeGrandTotal(infraTotal, foodTotal, drinksTotal, config.aufbau, config.abbau);
  const actualTotal = computeActualTotal(infraItems, foodItems, drinkItems);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <TotalHeader total={grandTotal} actualTotal={actualTotal} />

      <div style={{ padding: "16px 24px", flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Festival Kalkulation
          </Typography.Title>
          <Space>
            <ImportButton store={store} />
            <SnapshotManager store={store} />
          </Space>
        </div>

        {config.eventSegments.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Tage:{" "}
              {config.eventSegments.map((s) => `${s.name} (${s.attendeeCount} P.)`).join(" · ")}
              {" "}· Zusagen: {config.zusagenPercent}%
              {config.aufbau > 0 && ` · Aufbau: ${config.aufbau} €`}
              {config.abbau > 0 && ` · Abbau: ${config.abbau} €`}
            </Typography.Text>
          </div>
        )}

        <TagScenarioBar
          tags={tags}
          activeTagId={activeTagId}
          onSelect={(id) => store.setActiveTag(id)}
        />

        <Divider style={{ margin: "8px 0 16px" }} />

        <InfraSection store={store} />
        <FoodSection store={store} />
        <DrinksSection store={store} />

        {(config.aufbau > 0 || config.abbau > 0) && (
          <div style={{ textAlign: "right", marginTop: 8 }}>
            <Typography.Text type="secondary">
              + Aufbau {config.aufbau.toFixed(2)} € · Abbau {config.abbau.toFixed(2)} €
            </Typography.Text>
          </div>
        )}
      </div>
    </div>
  );
});
